import { useCallback, useSyncExternalStore } from 'react';

// Which editions this browser has opened. Kept in localStorage only: the spec deliberately has no server read state,
// so a reader on a new device sees everything as unread once.
const STORAGE_KEY = 'etn.releaseNotes.seen';
const MAX_KEYS = 200;
const listeners = new Set<() => void>();

function read(): string[] {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

let snapshot = typeof window === 'undefined' ? [] : read();

function subscribe(listener: () => void) {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY) return;
    snapshot = read();
    listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

export function markEditionSeen(key: string) {
  if (snapshot.includes(key)) return;
  snapshot = [key, ...snapshot].slice(0, MAX_KEYS);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Private mode or a full quota: the dot just comes back next visit.
  }
  listeners.forEach(listener => listener());
}

export function useSeenEditions() {
  const seen = useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => snapshot
  );
  const isSeen = useCallback((key: string) => seen.includes(key), [seen]);
  return { isSeen, markSeen: markEditionSeen };
}
